"use client";

import { c as _c } from "react/compiler-runtime";
import { getStripe } from "@/utils/stripe/client";
import { checkoutWithStripe } from "@/utils/stripe/server";
import { getErrorRedirect } from "@/utils/helpers";
import { useRouter, usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";
import { ArrowRight, Loader2, Plus } from "lucide-react";
import { useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { getTrackingPricing } from "@/utils/tracking/pricing";
import { createInitiateCheckoutEventId, getCheckoutTrackingContext } from "@/utils/tracking/checkout";
import { sendTikTokInitiateCheckoutEvent } from "@/actions/tiktokConversion";
import { getTrackingConsent } from "@/hooks/useTrackingConsent";
import { usePostHog } from "posthog-js/react";
import type { PublicOffer } from "@/lib/offers";
import { cn } from "@/lib/utils";
interface BuyEventButtonProps {
  className?: string;
  variant: "dark" | "light" | "lightest" | "christmas" | "white" | "konfirmation" | "barnedab" | "fodselsdag" | "barMitsva";
  packageType: "basic" | "premium";
  userEmail?: string;
  offerPlacement?: string;
  offer?: PublicOffer;
  /** Replaces "Buy event", e.g. "Choose Premium". */
  label?: string;
  /** "pill" is the landing page's rounded button with an arrow at the end. */
  appearance?: "default" | "pill";
  /** Colours for the pill's arrow circle. */
  arrowClassName?: string;
}
export default function BuyEventButton(t0) {
  const $ = _c(39);
  const {
    className,
    variant,
    packageType,
    userEmail,
    offerPlacement,
    offer,
    label,
    appearance: t1,
    arrowClassName
  } = t0;
  const appearance = t1 === undefined ? "default" : t1;
  const router = useRouter();
  const pathname = usePathname();
  const locale = useLocale();
  const posthog = usePostHog();
  const [submitting, setSubmitting] = useState(false);
  const priceId = packageType === "basic" ? process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_BASIC : process.env.NEXT_PUBLIC_STRIPE_PRICE_ID_PREMIUM;
  let t2;
  if ($[0] !== locale || $[1] !== offer || $[2] !== offerPlacement || $[3] !== packageType || $[4] !== pathname || $[5] !== posthog || $[6] !== priceId || $[7] !== router || $[8] !== userEmail) {
    t2 = async t3 => {
      const price = t3 === undefined ? {
        id: priceId
      } : t3;
      setSubmitting(true);
      const {
        checkoutFlow,
        checkoutPlace,
        checkoutLocale
      } = getCheckoutTrackingContext(pathname, locale);
      posthog.capture("clicked_buy_event", {
        package_type: packageType,
        packageType,
        platform: "web",
        checkout_flow: checkoutFlow,
        checkout_place: checkoutPlace,
        checkout_locale: checkoutLocale,
        is_upgrade: false,
        offer_placement: offerPlacement,
        offer_campaign_code: offer?.code
      });
      const checkoutLocaleValue = checkoutLocale;
      const fallbackPricing = getTrackingPricing(locale, packageType);
      const pricing = offer?.priceMinor === null || offer?.priceMinor === undefined ? fallbackPricing : {
        value: offer.priceMinor / 100,
        currency: offer.currency
      };
      const eventId = createInitiateCheckoutEventId();
      const hasAdvertisingConsent = getTrackingConsent().advertisement;
      if (hasAdvertisingConsent && window.fbq) {
        window.fbq("track", "InitiateCheckout", {
          value: pricing.value,
          currency: pricing.currency
        }, {
          eventID: eventId
        });
      }
      if (hasAdvertisingConsent && window.ttq) {
        window.ttq.track("InitiateCheckout", {
          value: pricing.value,
          currency: pricing.currency,
          contents: [{
            content_id: "guestgallery_subscription",
            content_type: "product",
            quantity: 1,
            price: pricing.value
          }],
          content_type: "product"
        });
      }
      if (hasAdvertisingConsent && window.gtag) {
        window.gtag("event", "begin_checkout", {
          value: pricing.value,
          currency: pricing.currency,
          items: [{
            item_id: "guestgallery_subscription",
            item_name: `Guest.Gallery ${packageType}`,
            quantity: 1,
            price: pricing.value
          }]
        });
      }
      if (userEmail) {
        sendTikTokInitiateCheckoutEvent(userEmail, checkoutLocaleValue, packageType, eventId);
      }
      const {
        errorRedirect,
        sessionId
      } = await checkoutWithStripe(price, "/thank-you", checkoutLocaleValue, checkoutPlace, packageType, offerPlacement, eventId);
      if (errorRedirect) {
        posthog.capture("checkout_redirect_failed", {
          package_type: packageType,
          checkout_flow: checkoutFlow,
          checkout_place: checkoutPlace,
          checkout_locale: checkoutLocaleValue,
          failure_stage: "session_creation",
          failure_reason: "error_redirect",
          platform: "web",
          offer_placement: offerPlacement,
          offer_campaign_code: offer?.code
        });
        return router.push(errorRedirect);
      }
      if (!sessionId) {
        posthog.capture("checkout_redirect_failed", {
          package_type: packageType,
          checkout_flow: checkoutFlow,
          checkout_place: checkoutPlace,
          checkout_locale: checkoutLocaleValue,
          failure_stage: "session_creation",
          failure_reason: "missing_session_id",
          platform: "web",
          offer_placement: offerPlacement
        });
        return router.push(getErrorRedirect("/home", "An unknown error occurred.", "Please try again later or contact a system administrator."));
      }
      const stripe = await getStripe();
      if (!stripe) {
        posthog.capture("checkout_redirect_failed", {
          package_type: packageType,
          checkout_flow: checkoutFlow,
          checkout_place: checkoutPlace,
          checkout_locale: checkoutLocaleValue,
          failure_stage: "stripe_initialization",
          failure_reason: "stripe_unavailable",
          stripe_checkout_session_id: sessionId,
          platform: "web",
          offer_placement: offerPlacement
        });
        setSubmitting(false);
        return;
      }
      const redirectResult = await stripe.redirectToCheckout({
        sessionId
      });
      if (redirectResult?.error) {
        posthog.capture("checkout_redirect_failed", {
          package_type: packageType,
          checkout_flow: checkoutFlow,
          checkout_place: checkoutPlace,
          checkout_locale: checkoutLocaleValue,
          failure_stage: "stripe_redirect",
          failure_reason: redirectResult.error.type || "stripe_error",
          stripe_checkout_session_id: sessionId,
          platform: "web",
          offer_placement: offerPlacement
        });
      }
      setSubmitting(false);
    };
    $[0] = locale;
    $[1] = offer;
    $[2] = offerPlacement;
    $[3] = packageType;
    $[4] = pathname;
    $[5] = posthog;
    $[6] = priceId;
    $[7] = router;
    $[8] = userEmail;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const handleStripeCheckout = t2;
  const t = useTranslations("Home.BuyEventCard");
  if (appearance === "pill") {
    let t3;
    if ($[10] !== className) {
      t3 = cn("group h-12 w-full justify-between gap-3 rounded-full py-1.5 pl-6 pr-1.5 text-base font-semibold transition-[transform,background-color] duration-150 ease-[var(--ease-out-strong)] active:scale-[0.97] disabled:opacity-100", className);
      $[10] = className;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    let t4;
    if ($[12] !== handleStripeCheckout) {
      t4 = () => {
        handleStripeCheckout();
      };
      $[12] = handleStripeCheckout;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    let t5;
    if ($[14] !== label || $[15] !== submitting || $[16] !== t) {
      t5 = submitting ? t("buttonLoading") : label ?? t("buyEvent");
      $[14] = label;
      $[15] = submitting;
      $[16] = t;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    let t6;
    if ($[18] !== arrowClassName) {
      t6 = cn("grid h-9 w-9 shrink-0 place-items-center rounded-full transition-transform duration-300 ease-[var(--ease-overshoot)] can-hover:group-hover:translate-x-0.5 can-hover:group-hover:-rotate-12", arrowClassName);
      $[18] = arrowClassName;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    let t7;
    if ($[20] !== submitting) {
      t7 = submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />;
      $[20] = submitting;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    let t8;
    if ($[22] !== t6 || $[23] !== t7) {
      t8 = <span aria-hidden="true" className={t6}>{t7}</span>;
      $[22] = t6;
      $[23] = t7;
      $[24] = t8;
    } else {
      t8 = $[24];
    }
    let t9;
    if ($[25] !== submitting || $[26] !== t3 || $[27] !== t4 || $[28] !== t5 || $[29] !== t8 || $[30] !== variant) {
      t9 = <Button className={t3} disabled={submitting} variant={variant} onClick={t4}>{t5}{t8}</Button>;
      $[25] = submitting;
      $[26] = t3;
      $[27] = t4;
      $[28] = t5;
      $[29] = t8;
      $[30] = variant;
      $[31] = t9;
    } else {
      t9 = $[31];
    }
    return t9;
  }
  let t3;
  if ($[32] !== className || $[33] !== handleStripeCheckout || $[34] !== label || $[35] !== submitting || $[36] !== t || $[37] !== variant) {
    t3 = <>{!submitting ? <Button className={className} variant={variant} onClick={() => {
        handleStripeCheckout();
      }}><span className="flex items-center"><Plus className="mr-2 h-4 w-4" />{label ?? t("buyEvent")}</span></Button> : <Button className={className} disabled={true} variant={variant}><span className="flex items-center"><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("buttonLoading")}</span></Button>}</>;
    $[32] = className;
    $[33] = handleStripeCheckout;
    $[34] = label;
    $[35] = submitting;
    $[36] = t;
    $[37] = variant;
    $[38] = t3;
  } else {
    t3 = $[38];
  }
  return t3;
}